import { useState, useEffect, useCallback } from "react";
import { subscribers as api, type Subscriber, type SubscriberStats, type EmailStatus } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import Spinner from "@/components/Spinner";
import SubscriberEmailsDialog from "@/components/SubscriberEmailsDialog";
import { UserPlus, Search, Trash2, X, ChevronDown, Sparkles, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, Upload, FileText, AlertCircle, CheckCircle2, Mail } from "lucide-react";

// ── Email status badge ────────────────────────────────────────────────────────
function EmailStatusBadge({
  status, onClick, loading,
}: {
  status?: EmailStatus; onClick: () => void; loading: boolean;
}) {
  if (loading) {
    return (
      <button disabled className="inline-flex items-center gap-1 text-xs text-slate opacity-50">
        <Loader2 size={12} className="animate-spin" /> Checking…
      </button>
    );
  }
  if (!status || status === "unknown") {
    return (
      <button
        onClick={onClick}
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate hover:text-coral transition-colors"
        title="Run email validation"
      >
        <ShieldQuestion size={13} />
        <span>Validate</span>
      </button>
    );
  }
  const cfg = {
    valid:   { icon: ShieldCheck,  cls: "text-green-600", label: "Valid"   },
    risky:   { icon: ShieldAlert,  cls: "text-amber-600", label: "Risky"   },
    invalid: { icon: ShieldAlert,  cls: "text-red-500",   label: "Invalid" },
  }[status] ?? { icon: ShieldQuestion, cls: "text-slate", label: "Unknown" };
  const Icon = cfg.icon;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs font-semibold ${cfg.cls} hover:opacity-70 transition-opacity`}
      title="Re-validate"
    >
      <Icon size={13} />
      <span>{cfg.label}</span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SubscribersPage() {
  const [items, setItems]               = useState<Subscriber[]>([]);
  const [stats, setStats]               = useState<SubscriberStats | null>(null);
  const [nextToken, setNextToken]       = useState<string | null>(null);
  const [loading, setLoading]           = useState(true);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tierFilter, setTierFilter]     = useState("");
  const [showAdd, setShowAdd]           = useState(false);
  const [newEmail, setNewEmail]         = useState("");
  const [newName, setNewName]           = useState("");
  const [newTags, setNewTags]           = useState("");
  const [adding, setAdding]             = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Enrichment state
  const [enrichingId, setEnrichingId]   = useState<string | null>(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [enrichedIds, setEnrichedIds]   = useState<Set<string>>(new Set());

  // Validation state
  const [validatingId, setValidatingId]   = useState<string | null>(null);
  const [bulkValidating, setBulkValidating] = useState(false);
  // Local overrides so UI updates immediately without a full reload
  const [emailStatusOverrides, setEmailStatusOverrides] = useState<Record<string, EmailStatus>>({});

  // Batch import state
  const [showImport, setShowImport]     = useState(false);
  const [importText, setImportText]     = useState("");
  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; total: number } | null>(null);
  const [importError, setImportError]   = useState<string | null>(null);
  const [importPreview, setImportPreview] = useState<Array<{ email: string; name?: string; tags?: string[] }>>([]);
  const [importParseError, setImportParseError] = useState<string | null>(null);

  // Shared status message bar
  const [statusMsg, setStatusMsg] = useState<{ text: string; type: "info" | "error" } | null>(null);

  // Sent emails dialog
  const [emailsFor, setEmailsFor] = useState<{ id: string; email: string } | null>(null);

  const showMsg = (text: string, type: "info" | "error" = "info") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg(null), 6000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.list({ search: search || undefined, status: statusFilter || undefined, engagement_tier: (tierFilter as any) || undefined, maxResults: 50 }),
        api.stats(),
      ]);
      setItems(listRes.subscribers);
      setNextToken(listRes.nextToken);
      setStats(statsRes.stats);
      // Clear overrides after a fresh load — the DB values are now accurate
      setEmailStatusOverrides({});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, statusFilter, tierFilter]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextToken) return;
    setLoadingMore(true);
    try {
      const res = await api.list({ search: search || undefined, status: statusFilter || undefined, engagement_tier: (tierFilter as any) || undefined, maxResults: 50, nextToken });
      setItems((prev) => [...prev, ...res.subscribers]);
      setNextToken(res.nextToken);
    } catch (e) { console.error(e); }
    finally { setLoadingMore(false); }
  };

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Parse CSV/plain-text input into subscriber objects
  const parseImportText = (text: string): Array<{ email: string; name?: string; tags?: string[] }> => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    // Detect if first line looks like a header
    const firstLower = lines[0]?.toLowerCase() ?? "";
    const hasHeader = firstLower.includes("email") || firstLower.includes("name");
    const dataLines = hasHeader ? lines.slice(1) : lines;

    return dataLines.map((line) => {
      // Split on comma or tab, but respect quoted fields
      const cols = line.match(/("(?:[^"]|"")*"|[^,\t]+|(?<=,|^)(?=,|$))/g)?.map((c) =>
        c.replace(/^"|"$/g, "").replace(/""/g, '"').trim()
      ) ?? [line.trim()];

      const email = cols[0] ?? "";
      const name = cols[1] || undefined;
      const rawTags = cols[2] || "";
      const tags = rawTags ? rawTags.split(/[;|]/).map((t) => t.trim()).filter(Boolean) : undefined;
      return { email, name, tags };
    }).filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email));
  };

  const handleImportTextChange = (text: string) => {
    setImportText(text);
    setImportResult(null);
    setImportError(null);
    if (!text.trim()) { setImportPreview([]); setImportParseError(null); return; }
    try {
      const parsed = parseImportText(text);
      if (parsed.length === 0) {
        setImportParseError("No valid email addresses found. Make sure each line has a valid email.");
        setImportPreview([]);
      } else {
        setImportParseError(null);
        setImportPreview(parsed.slice(0, 5));
      }
    } catch {
      setImportParseError("Could not parse input.");
      setImportPreview([]);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      handleImportTextChange(text);
    };
    reader.readAsText(file);
    // Reset input so same file can be re-selected
    e.target.value = "";
  };

  const handleBatchImport = async () => {
    const parsed = parseImportText(importText);
    if (parsed.length === 0) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const result = await api.import({ subscribers: parsed });
      setImportResult(result);
      load();
    } catch (e: any) {
      setImportError(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.create({
        email: newEmail,
        name: newName || undefined,
        tags: newTags ? newTags.split(",").map((t) => t.trim()) : undefined,
      });
      setShowAdd(false);
      setNewEmail(""); setNewName(""); setNewTags("");
      load();
    } catch (e) { console.error(e); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this subscriber?")) return;
    setActionLoading(id);
    try { await api.delete(id); load(); }
    catch (e) { console.error(e); }
    finally { setActionLoading(null); }
  };

  const handleEnrich = async (id: string) => {
    setEnrichingId(id);
    try {
      await api.enrich(id);
      setEnrichedIds((prev) => new Set([...prev, id]));
    } catch (e: any) {
      console.error("Enrich failed:", e);
    } finally {
      setEnrichingId(null);
    }
  };

  const handleBulkEnrich = async () => {
    setBulkEnriching(true);
    try {
      const res = await api.bulkEnrich();
      showMsg(res.message);
    } catch (e: any) {
      showMsg(e.message || "Bulk enrich failed", "error");
    } finally {
      setBulkEnriching(false);
    }
  };

  const handleValidate = async (id: string) => {
    setValidatingId(id);
    try {
      const res = await api.validate(id);
      // Update local override so UI reflects the new status immediately
      setEmailStatusOverrides((prev) => ({ ...prev, [id]: res.result.status }));
    } catch (e: any) {
      showMsg(e.message || "Validation failed", "error");
    } finally {
      setValidatingId(null);
    }
  };

  const handleBulkValidate = async () => {
    setBulkValidating(true);
    try {
      const res = await api.bulkValidate();
      showMsg(res.message + " — results will appear as each check completes.");
      // Reload after a short delay to pick up completed results
      setTimeout(() => load(), 8000);
    } catch (e: any) {
      showMsg(e.message || "Bulk validate failed", "error");
    } finally {
      setBulkValidating(false);
    }
  };

  const statCards = stats ? [
    { label: "Total",        value: stats.total,        color: "text-ink"      },
    { label: "Active",       value: stats.active,       color: "text-teal"     },
    { label: "Unsubscribed", value: stats.unsubscribed, color: "text-red-500"  },
    { label: "New (30d)",    value: stats.new_last_30d, color: "text-coral"    },
  ] : [];

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Subscribers</h1>
          <p className="text-sm text-slate">{items.length} loaded{nextToken ? " (more available)" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleBulkValidate} disabled={bulkValidating}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 disabled:opacity-60 transition-colors">
            <ShieldCheck size={15} className={bulkValidating ? "animate-pulse text-green-500" : ""} />
            {bulkValidating ? "Validating…" : "Validate All"}
          </button>
          <button onClick={handleBulkEnrich} disabled={bulkEnriching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 disabled:opacity-60 transition-colors">
            <Sparkles size={15} className={bulkEnriching ? "animate-pulse text-coral" : ""} />
            {bulkEnriching ? "Enriching…" : "Enrich All"}
          </button>
          <button onClick={() => { setShowImport(true); setImportText(""); setImportPreview([]); setImportResult(null); setImportError(null); setImportParseError(null); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 transition-colors">
            <Upload size={15} /> Import CSV
          </button>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark transition-colors shadow-md shadow-coral/20">
            <UserPlus size={16} /> Add Subscriber
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        {/* Stat cards */}
        {statCards.length > 0 && (
          <div className="flex gap-4">
            {statCards.map((s) => (
              <div key={s.label} className="bg-white rounded-xl px-5 py-3 border border-black/[0.06] flex items-center gap-3">
                <span className={`font-display text-2xl font-bold ${s.color}`}>{Number(s.value).toLocaleString()}</span>
                <span className="text-xs font-semibold text-slate uppercase tracking-wider">{s.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Search + filters */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          </div>
          <div className="flex gap-2">
            {["", "active", "unsubscribed", "bounced"].map((f) => (
              <button key={f || "all"} onClick={() => setStatusFilter(f)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors ${
                  statusFilter === f ? "bg-ink text-white" : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
                }`}>{f || "All"}</button>
            ))}
          </div>
          <div className="flex gap-2 ml-2 pl-2 border-l border-black/[0.06]">
            {([
              { v: "",     label: "Any" },
              { v: "hot",  label: "🔥 Hot" },
              { v: "warm", label: "☀️ Warm" },
              { v: "cold", label: "❄️ Cold" },
              { v: "new",  label: "🆕 New" },
            ] as const).map((f) => (
              <button key={f.v || "any"} onClick={() => setTierFilter(f.v)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  tierFilter === f.v
                    ? f.v === "hot" ? "bg-red-500 text-white" : f.v === "warm" ? "bg-amber-500 text-white" : f.v === "cold" ? "bg-blue-500 text-white" : "bg-ink text-white"
                    : "bg-white text-slate border border-black/[0.06] hover:border-black/20"
                }`}>{f.label}</button>
            ))}
          </div>
        </div>

        {/* Add subscriber modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
              <button onClick={() => setShowAdd(false)} className="absolute top-4 right-4 text-slate hover:text-ink"><X size={20} /></button>
              <h2 className="text-lg font-bold mb-4">Add Subscriber</h2>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1.5">Email</label>
                  <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                    placeholder="subscriber@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1.5">Name</label>
                  <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                    placeholder="Jane Doe" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate uppercase tracking-wider mb-1.5">Tags (comma separated)</label>
                  <input value={newTags} onChange={(e) => setNewTags(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
                    placeholder="vip, newsletter" />
                </div>
                <div className="flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setShowAdd(false)}
                    className="px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.08] hover:border-black/20">Cancel</button>
                  <button type="submit" disabled={adding}
                    className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
                    {adding ? "Adding..." : "Add Subscriber"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Batch import modal */}
        {showImport && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
              <button onClick={() => setShowImport(false)} className="absolute top-4 right-4 text-slate hover:text-ink"><X size={20} /></button>
              <div className="flex items-center gap-2 mb-1">
                <Upload size={18} className="text-coral" />
                <h2 className="text-lg font-bold">Batch Import Subscribers</h2>
              </div>
              <p className="text-sm text-slate mb-4">Paste CSV data or upload a file. Columns: <code className="bg-paper px-1 rounded text-xs">email, name (optional), tags (optional, semicolon-separated)</code></p>

              {/* File upload */}
              <label className="flex items-center gap-2 w-fit mb-3 px-4 py-2 rounded-lg border border-dashed border-black/20 text-sm font-semibold text-slate hover:border-coral hover:text-coral cursor-pointer transition-colors">
                <FileText size={15} />
                Upload CSV file
                <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFileUpload} />
              </label>

              <div className="relative">
                <textarea
                  value={importText}
                  onChange={(e) => handleImportTextChange(e.target.value)}
                  rows={10}
                  placeholder={"email,name,tags\njane@example.com,Jane Doe,newsletter;vip\nbob@example.com,Bob Smith"}
                  className="w-full px-4 py-3 rounded-xl border border-black/[0.08] bg-paper text-sm font-mono focus:outline-none focus:ring-2 focus:ring-coral/30 resize-none"
                />
              </div>

              {/* Parse error */}
              {importParseError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertCircle size={14} className="shrink-0" />
                  {importParseError}
                </div>
              )}

              {/* Preview */}
              {importPreview.length > 0 && !importParseError && !importResult && (
                <div className="mt-3">
                  <p className="text-xs font-semibold text-slate uppercase tracking-wider mb-2">
                    Preview — {parseImportText(importText).length} valid subscriber{parseImportText(importText).length !== 1 ? "s" : ""} detected
                    {parseImportText(importText).length > 5 ? ` (showing first 5)` : ""}
                  </p>
                  <div className="rounded-xl border border-black/[0.06] overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-black/[0.02]">
                          {["Email", "Name", "Tags"].map((h) => (
                            <th key={h} className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate border-b border-black/[0.06]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.map((s, i) => (
                          <tr key={i} className="border-b border-black/[0.04] last:border-0">
                            <td className="px-3 py-2 font-semibold">{s.email}</td>
                            <td className="px-3 py-2 text-slate">{s.name || "—"}</td>
                            <td className="px-3 py-2 text-slate">{s.tags?.join(", ") || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Import result */}
              {importResult && (
                <div className="mt-3 flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-3">
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Import complete!</p>
                    <p className="text-green-600">{importResult.imported} imported · {importResult.skipped} skipped (already exist) · {importResult.total} total</p>
                  </div>
                </div>
              )}

              {/* Import error */}
              {importError && (
                <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle size={14} className="shrink-0" />
                  {importError}
                </div>
              )}

              <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-black/[0.06]">
                <button onClick={() => setShowImport(false)}
                  className="px-5 py-2.5 rounded-full text-sm font-semibold border border-black/[0.08] hover:border-black/20">
                  {importResult ? "Close" : "Cancel"}
                </button>
                {!importResult && (
                  <button
                    onClick={handleBatchImport}
                    disabled={importing || parseImportText(importText).length === 0}
                    className="px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20 inline-flex items-center gap-2">
                    {importing ? <><Loader2 size={14} className="animate-spin" /> Importing…</> : <><Upload size={14} /> Import {parseImportText(importText).length > 0 ? `${parseImportText(importText).length} subscribers` : ""}</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}


        {statusMsg && (
          <div className={`p-3 rounded-xl text-sm flex items-center justify-between ${
            statusMsg.type === "error"
              ? "bg-red-50 border border-red-200 text-red-700"
              : "bg-blue-50 border border-blue-200 text-blue-700"
          }`}>
            <span>{statusMsg.text}</span>
            <button onClick={() => setStatusMsg(null)} className="ml-3 opacity-60 hover:opacity-100"><X size={14} /></button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <Spinner className="py-20" />
        ) : (
          <>
            <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-black/[0.02]">
                    {["Email", "Name", "Status", "Score", "Email Valid", "Tags", "Enriched", "Subscribed", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate border-b border-black/[0.06]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    // Prefer local override (set after a per-row validate) over DB value
                    const emailStatus = (emailStatusOverrides[s.id] ?? s.email_status) as EmailStatus | undefined;
                    return (
                      <tr key={s.id} className={`border-b border-black/[0.04] hover:bg-black/[0.01] ${
                        emailStatus === "invalid" ? "bg-red-50/40" : ""
                      }`}>
                        <td className="px-4 py-3 font-semibold max-w-[180px] truncate">{s.email}</td>
                        <td className="px-4 py-3 text-slate">{s.name || "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-xs font-bold ${
                              s.engagement_tier === "hot" ? "text-red-500" :
                              s.engagement_tier === "warm" ? "text-amber-500" :
                              s.engagement_tier === "cold" ? "text-blue-500" : "text-slate/50"
                            }`}>
                              {s.engagement_score ?? "—"}
                            </span>
                            <span className="text-[10px]">
                              {s.engagement_tier === "hot" ? "🔥" : s.engagement_tier === "warm" ? "☀️" : s.engagement_tier === "cold" ? "❄️" : ""}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <EmailStatusBadge
                            status={emailStatus}
                            loading={validatingId === s.id}
                            onClick={() => handleValidate(s.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {(Array.isArray(s.tags) ? s.tags : []).map((t) => (
                              <span key={t} className="px-1.5 py-0.5 bg-paper rounded text-[11px] font-semibold text-slate">{t}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {enrichedIds.has(s.id) ? (
                            <span className="text-xs font-semibold text-green-600">✓</span>
                          ) : (
                            <button onClick={() => handleEnrich(s.id)} disabled={enrichingId === s.id}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-slate hover:text-coral disabled:opacity-50 transition-colors">
                              <Sparkles size={11} className={enrichingId === s.id ? "animate-pulse text-coral" : ""} />
                              {enrichingId === s.id ? "…" : "Enrich"}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate text-xs whitespace-nowrap">
                          {new Date(s.subscribed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setEmailsFor({ id: s.id, email: s.email })}
                              className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                              title="View sent emails"
                            >
                              <Mail size={14} />
                            </button>
                            <button onClick={() => handleDelete(s.id)} disabled={actionLoading === s.id}
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors" title="Remove">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr><td colSpan={9} className="px-6 py-16 text-center text-slate">
                      {search ? "No subscribers match your search." : "No subscribers yet. Add your first one!"}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {nextToken && (
              <div className="flex justify-center mt-6">
                <button onClick={loadMore} disabled={loadingMore}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/20 hover:bg-white transition-colors disabled:opacity-60">
                  <ChevronDown size={16} />
                  {loadingMore ? "Loading..." : "Load more subscribers"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {emailsFor && (
        <SubscriberEmailsDialog
          subscriberId={emailsFor.id}
          subscriberEmail={emailsFor.email}
          onClose={() => setEmailsFor(null)}
        />
      )}
    </div>
  );
}
